import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Send, Sunrise, Sun, Sunset, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, pick, waLink } from "@/i18n";
import { useAuth } from "@/auth";
import { track } from "@/utils/track";
import { useSEO } from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMS = [
  { key: "culture", categories: ["culture"], slot: "morning" },
  { key: "nature", categories: ["nature", "canoe"], slot: "morning" },
  { key: "sea", categories: ["sea", "canoe"], slot: "afternoon" },
  { key: "taste", categories: ["food", "wine"], slot: "evening" },
];

const DIM_LABEL = {
  culture: { it: "Cultura", en: "Culture" },
  nature: { it: "Natura", en: "Nature" },
  sea: { it: "Mare", en: "Sea" },
  taste: { it: "Gusto", en: "Taste" },
};

export default function DayBuilder() {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [dims, setDims] = useState([]);
  const [area, setArea] = useState("");
  const [people, setPeople] = useState(2);
  const [itinerary, setItinerary] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [sent, setSent] = useState(false);

  useSEO(`${t("day_title")} — Flegrea Experience`, t("day_sub"));

  useEffect(() => {
    fetch(`${API}/experiences`).then((r) => r.json()).then(setExperiences).catch(() => {});
    fetch(`${API}/destinations`).then((r) => r.json()).then(setDestinations).catch(() => {});
  }, []);

  const toggleDim = (key) => setDims((d) => (d.includes(key) ? d.filter((x) => x !== key) : [...d, key]));

  const generate = () => {
    if (dims.length < 2) {
      toast(t("day_empty"));
      return;
    }
    const slots = { morning: [], afternoon: [], evening: [] };
    const used = new Set();
    dims.forEach((dk) => {
      const dim = DIMS.find((d) => d.key === dk);
      const pool = experiences
        .filter((e) => e.categories.some((c) => dim.categories.includes(c)))
        .filter((e) => !area || e.destination === area)
        .filter((e) => !used.has(e.slug))
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      if (pool.length) {
        slots[dim.slot].push(pool[0]);
        used.add(pool[0].slug);
      }
    });
    if (!slots.morning.length && !slots.afternoon.length && !slots.evening.length) {
      toast(lang === "it" ? "Nessuna esperienza disponibile per questa combinazione." : "No experiences available for this combination.");
      return;
    }
    setItinerary(slots);
    track("day_builder_generate", { dims, area });
  };

  const picked = itinerary ? [...itinerary.morning, ...itinerary.afternoon, ...itinerary.evening] : [];
  const itineraryText = () =>
    picked.map((e) => `- ${pick(e.title, lang)} (${pick(e.duration, lang)})`).join("\n");

  const waMessage = () =>
    waLink(`${t("wa_message")}\n${t("day_itinerary")}:\n${itineraryText()}\n${t("day_people")}: ${people}`);

  const submit = async () => {
    if (!user && (!guestName || !guestEmail)) {
      toast(t("login_to_save"));
      return;
    }
    try {
      const r = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          people: String(people),
          loves: dims.map((d) => DIM_LABEL[d].en),
          where: area ? [pick(destinations.find((x) => x.slug === area)?.name, lang)] : [],
          notes: `${t("day_itinerary")}:\n${itineraryText()}`,
          name: guestName || user?.name || "—",
          email: guestEmail || user?.email || "—",
          language: lang,
        }),
      });
      if (!r.ok) throw new Error();
      setSent(true);
      track("day_builder_submit", { dims, area, people });
    } catch {
      toast.error("Error");
    }
  };

  const slotMeta = [
    ["morning", t("day_morning"), Sunrise],
    ["afternoon", t("day_afternoon"), Sun],
    ["evening", t("day_evening"), Sunset],
  ];

  return (
    <div className="py-16 lg:py-24" data-testid="day-builder-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/create" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/40 hover:text-ink transition-colors duration-300 mb-8">
          <ArrowLeft size={13} strokeWidth={1.5} /> {t("create_title")}
        </Link>
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-medblue mb-4">Bespoke</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight" data-testid="day-title">{t("day_title")}</h1>
        <p className="mt-4 text-ink/60 text-base md:text-lg">{t("day_sub")}</p>

        {sent ? (
          <div className="mt-14 text-center border border-sage/60 p-10" data-testid="day-sent">
            <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-5">
              <Check size={24} strokeWidth={1.5} className="text-sage" />
            </div>
            <h2 className="font-serif text-3xl text-ink">{t("day_sent_title")}</h2>
            <p className="mt-3 text-sm text-ink/60">{t("day_sent_sub")}</p>
            <a href={waMessage()} target="_blank" rel="noopener noreferrer" data-testid="day-sent-whatsapp"
              className="mt-6 inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.15em] px-6 py-3.5 hover:bg-ink transition-colors duration-300">
              <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
            </a>
          </div>
        ) : !itinerary ? (
          <div className="mt-12 space-y-10">
            <div>
              <h2 className="text-[11px] uppercase tracking-[0.25em] text-ink/40 mb-4">{t("day_pick")}</h2>
              <div className="flex flex-wrap gap-3">
                {DIMS.map((d) => (
                  <button key={d.key} data-testid={`day-dim-${d.key}`} onClick={() => toggleDim(d.key)}
                    className={`font-serif text-2xl px-6 py-4 border transition-colors duration-300 ${dims.includes(d.key) ? "bg-ink text-ivory border-ink" : "border-stone2 text-ink/70 hover:border-ink"}`}>
                    {DIM_LABEL[d.key][lang]}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h2 className="text-[11px] uppercase tracking-[0.25em] text-ink/40 mb-4">{t("day_area")}</h2>
                <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="day-area"
                  className="w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue">
                  <option value="">{t("day_area_all")}</option>
                  {destinations.map((d) => <option key={d.slug} value={d.slug}>{pick(d.name, lang)}</option>)}
                </select>
              </div>
              <div>
                <h2 className="text-[11px] uppercase tracking-[0.25em] text-ink/40 mb-4">{t("day_people")}</h2>
                <select value={people} onChange={(e) => setPeople(Number(e.target.value))} data-testid="day-people"
                  className="w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue">
                  {[...Array(10)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                </select>
              </div>
            </div>
            <button onClick={generate} data-testid="day-generate"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-10 py-5 hover:bg-ink transition-colors duration-300">
              {t("day_generate")}
            </button>
          </div>
        ) : (
          <div className="mt-12" data-testid="day-itinerary">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-3xl text-ink">{t("day_itinerary")}</h2>
              <button onClick={() => setItinerary(null)} data-testid="day-edit" className="text-xs uppercase tracking-[0.2em] text-medblue hover:text-ink transition-colors duration-300">
                {t("day_edit")}
              </button>
            </div>
            <div className="space-y-6">
              {slotMeta.map(([slot, label, Icon]) =>
                itinerary[slot].map((e) => (
                  <div key={e.slug} className="border border-stone2/60 p-5 flex gap-5 items-center" data-testid={`day-slot-${e.slug}`}>
                    <div className="w-20 h-20 overflow-hidden shrink-0 hidden sm:block">
                      <img src={e.image} alt={pick(e.title, lang)} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-terracotta mb-1">
                        <Icon size={13} strokeWidth={1.5} /> {label}
                      </p>
                      <Link to={`/experiences/${e.slug}`} className="font-serif text-xl text-ink hover:text-medblue transition-colors duration-300">
                        {pick(e.title, lang)}
                      </Link>
                      <p className="text-xs text-ink/50 mt-1">{pick(e.duration, lang)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            {!user && (
              <div className="grid sm:grid-cols-2 gap-3 mt-6">
                <input placeholder={t("name_label")} value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="day-guest-name"
                  className="border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue" />
                <input type="email" placeholder={t("email_label")} value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} data-testid="day-guest-email"
                  className="border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue" />
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={submit} data-testid="day-send"
                className="inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink transition-colors duration-300">
                <Send size={14} strokeWidth={1.5} /> {t("day_send")}
              </button>
              <a href={waMessage()} target="_blank" rel="noopener noreferrer" data-testid="day-whatsapp"
                className="inline-flex items-center gap-2 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300">
                <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
